import { useEffect, useRef, startTransition, useCallback } from 'react';
import { applyElkLayoutResult, createElkCommitHelpers } from './applyElkLayoutResult';
import { computeElkLayoutResult } from './computeElkLayoutResult';
import { clearElkLayoutCache } from './elkLayoutCache';

/**
 * Single-campaign async ELK layout: runs when layout generation or inputs change.
 */
export function useElkAsyncLayoutEffect({
  isMultiCampaignMode,
  tasksForLayoutRef,
  nodeDimensionsRef,
  chainOrder,
  customPositionsRef,
  clearCustomPositionsBeforeElkApplyRef,
  layoutVersion,
  layoutVersionRef,
  lockedTaskIdSignature,
  lockedDependencySignature,
  scheduleLayoutEdgeRevealAfter,
  startLayoutEdgeHiddenPhase,
  setElkLayoutResult,
  initialElkSettlementRef,
  lastCommittedElkPositionsRef,
  lastCommittedElkEdgePathsRef = null,
  lastCommittedLayoutPinKeyRef = null,
  layoutRequestModeRef = null,
  campaignId = null,
  connectLayoutCenterRef = null,
  pendingCenterTaskIdRef = null,
  layoutHoldRef = null,
  endLayoutHold = null,
}) {
  const chainOrderKey = chainOrder ? chainOrder.join(',') : null;

  /** Skip `calculateLayoutElk` when graph inputs match the last successful worker run (redundant Tidy). */
  const lastElkInputFingerprintRef = useRef(null);
  const lastElkPlainResultRef = useRef(null);
  const elkCacheCampaignRef = useRef(null);

  const commitElk = useCallback((value) => {
    startTransition(() => {
      setElkLayoutResult(value);
    });
  }, [setElkLayoutResult]);

  useEffect(() => {
    if (isMultiCampaignMode) {
      clearElkLayoutCache(lastElkInputFingerprintRef, lastElkPlainResultRef);
      commitElk(null);
      return undefined;
    }

    if (!tasksForLayoutRef.current?.length) {
      clearElkLayoutCache(lastElkInputFingerprintRef, lastElkPlainResultRef);
      commitElk(null);
      return undefined;
    }

    if (elkCacheCampaignRef.current != null && campaignId != null && elkCacheCampaignRef.current !== campaignId) {
      clearElkLayoutCache(lastElkInputFingerprintRef, lastElkPlainResultRef);
    }
    if (campaignId != null) {
      elkCacheCampaignRef.current = campaignId;
    }

    let cancelled = false;
    const layoutVersionWhenStarted = layoutVersionRef.current;
    const layoutRequestMode = layoutRequestModeRef?.current ?? null;
    const isExplicitAnimatedLayout = layoutRequestMode === 'animate';
    const isInstantLayout = layoutRequestMode === 'instant';

    // Connect / delete / Tidy already queued an animated pass. Implicit and instant
    // follow-ups (dims, post-measure, dep churn) snap over that slide if we let them run.
    // `pendingCenterTaskIdRef` is cleared when the camera starts — hold stays until FLIP ends.
    if (
      !isExplicitAnimatedLayout &&
      (layoutHoldRef?.current || pendingCenterTaskIdRef?.current)
    ) {
      return undefined;
    }

    const isCancelled = () => cancelled;
    const { finishExplicitHold, revealEdgesAfterExplicitTidyIfNeeded, commitElkBeforeReveal } =
      createElkCommitHelpers({
        isExplicitAnimatedLayout,
        endLayoutHold,
        scheduleLayoutEdgeRevealAfter,
        setElkLayoutResult,
        commitElk,
      });

    (async () => {
      try {
        const computed = await computeElkLayoutResult({
          isCancelled,
          layoutVersionWhenStarted,
          layoutVersionRef,
          isExplicitAnimatedLayout,
          tasksForLayoutRef,
          nodeDimensionsRef,
          customPositionsRef,
          lastCommittedElkPositionsRef,
          lastCommittedElkEdgePathsRef,
          lastCommittedLayoutPinKeyRef,
          lastElkInputFingerprintRef,
          lastElkPlainResultRef,
          initialElkSettlementRef,
          lockedTaskIdSignature,
          lockedDependencySignature,
          chainOrder,
          chainOrderKey,
        });
        if (!computed || computed.aborted) {
          // Explicit Tidy already hid edges + showed the blocker. A superseded
          // pass (newer layoutVersion) is owned by the replacement effect. If
          // this is still the current generation, nothing else will reveal.
          if (
            isExplicitAnimatedLayout &&
            layoutVersionWhenStarted === layoutVersionRef.current
          ) {
            revealEdgesAfterExplicitTidyIfNeeded();
            finishExplicitHold();
          }
          return;
        }

        applyElkLayoutResult({
          result: computed.result,
          layoutTasks: computed.layoutTasks,
          isCancelled,
          layoutVersionWhenStarted,
          layoutVersionRef,
          layoutRequestModeRef,
          initialElkSettlementRef,
          lastCommittedElkPositionsRef,
          customPositionsRef,
          clearCustomPositionsBeforeElkApplyRef,
          connectLayoutCenterRef,
          isExplicitAnimatedLayout,
          isInstantLayout,
          commitElk,
          commitElkBeforeReveal,
          revealEdgesAfterExplicitTidyIfNeeded,
          finishExplicitHold,
          startLayoutEdgeHiddenPhase,
          scheduleLayoutEdgeRevealAfter,
          setElkLayoutResult,
        });
      } catch (err) {
        console.error('[useFlowchartLayout] ELK layout failed:', err);
        if (!cancelled && layoutVersionWhenStarted === layoutVersionRef.current) {
          commitElk(null);
          revealEdgesAfterExplicitTidyIfNeeded();
          finishExplicitHold();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layoutVersion,
    lockedTaskIdSignature,
    lockedDependencySignature,
    isMultiCampaignMode,
    chainOrderKey,
    scheduleLayoutEdgeRevealAfter,
    startLayoutEdgeHiddenPhase,
    campaignId,
    commitElk,
  ]);
}
